/**
 * The season strip in the header: four chevrons and one honest figure beside them.
 *
 * Pass 14 replaces the five-stop flow that grew a distance ramp, per-end padding compensation and
 * a 1340px fallback. What is drawn now is simpler and says the same thing: four segments in a
 * rounded track, one lit, the past behind it and the future ahead.
 *
 * THE SEAM IS THE PAGE. The old band overlapped its steps by the width of the point, so two
 * chevrons of one colour had an invisible join and the distance ramp existed to make the arrows
 * read at all. Three pixels of page ground between segments does that job without a ramp, which
 * means the three colours can be flat and mean one thing each: behind you, here, ahead.
 *
 * THE PHONE LABELS ONE SEGMENT. Not because the others do not fit, but because "3 of 4" needs the
 * other three present to mean anything and does not need them named. They stay as stubs.
 */

import { PHASES, type Phase } from '../domain/phase.js';

export function PhaseFlow({ phase }: { phase: Phase }) {
  const at = PHASES.findIndex((p) => p.id === phase);
  return (
    <span
      className="pflow"
      role="img"
      aria-label={`Season phase: ${PHASES[at]?.long ?? 'unknown'}`}
    >
      {PHASES.map((p, i) => (
        <span
          key={p.id}
          className={`seg ${i < at ? 'past' : i === at ? 'now' : 'next'}`}
          title={p.long}
        >
          <span className="lbl">{p.long}</span>
        </span>
      ))}
    </span>
  );
}

export interface CountdownInfo {
  /** Whole days to an ANNOUNCED draft. Meaningless when `announced` is false. */
  days: number;
  /** Whether the commissioners have actually named a date. */
  announced: boolean;
  /** The regular-season week, once there is one. */
  week: number | null;
  phase: Phase;
}

/**
 * The countdown slot, and its whole job is to be honest about which of four things it knows.
 *
 * The old strip counted to the draft in every phase, including the seven months when no date had
 * been announced and it was quietly counting to a guess dressed as a fact. Four states now:
 *
 *   an announced draft ahead of us   `DRAFT IN 8d`
 *   nothing announced at all         `DRAFT NOT SET`
 *   the season running               `WEEK 6`
 *   draft day                        nothing
 *
 * On draft day the slot is empty because the live strip sits directly underneath carrying the
 * pick clock, and a second number in the same eyeline counting the same event down to the day is
 * the duplication this pass exists to remove.
 *
 * The projection has not gone; it moved to where it can explain itself. The deadline rail's foot
 * still says the app is counting to the Tuesday after Labor Day and that it is a guess. A header
 * slot has no room for that sentence, so it declines to imply it.
 */
export function Countdown({ info }: { info: CountdownInfo }) {
  if (info.phase === 'draftday') return null;

  if (info.phase === 'inseason') {
    /* No week means the app cannot say which one it is. It says nothing rather than a number. */
    if (info.week === null) return null;
    return (
      <span className="cdown" aria-label={`Week ${info.week} of the season`}>
        <b>WEEK {info.week}</b>
      </span>
    );
  }

  if (!info.announced) {
    return (
      <span className="cdown unset" aria-label="No draft date announced">
        <b>DRAFT NOT SET</b>
      </span>
    );
  }

  return (
    <span className="cdown" aria-label={`Draft in ${info.days} days`}>
      <b>DRAFT IN {info.days}d</b>
    </span>
  );
}
